<script lang="ts" setup>
	import StringMethods from '~/utils/character'
	import type { FooterContext } from '../footer/link-structure.vue'

	const props = defineProps<{
		variant?: string
	}>()
	const { toLower } = StringMethods()
	const viewKey = computed(() => toLower(props.variant || 'sendcare') as 'sendcare' | NAVLINKS.sendcareWaitlist)
	const { sendcareLinks, productFilterKey } = storeToRefs(useProductsStore())
	const footerLinks = ref<FooterContext[]>([
		{
			title: 'Products',
			open: true,
			links: sendcareLinks.value,
			disabled: viewKey.value != 'sendcare'
		},
		{
			title: 'Company',
			links: [
				{
					url: '/privacy-policy',
					label: 'Privacy Policy'
				},
				{
					url: '/cookie-policy',
					label: 'Cookies Policy'
				},
				{
					url: '/terms-and-conditions',
					label: 'Terms & Conditions'
				}
			],
			disabled: viewKey.value != 'sendcare',
			open: false
		},
		{
			title: 'Contact Us',
			links: [
				{
					url: 'tel:+234 9070008899',
					label: '+234 9070008899'
				},
				{
					url: 'mailto:hello@mycovergenius.com',
					label: 'hello@mycovergenius.com'
				},
				{
					label: '8 The Providence Street, Lekki Phase 1, Lagos, Nigeria'
				}
			],
			open: false
		}
	])
	const socials = [
		{
			icon: 'x',
			link: '',
			height: '25.42px',
			width: '24.86px'
		},
		{
			icon: 'facebook-2',
			link: '',
			height: '34.95px',
			width: '33.95px'
		},
		{
			icon: 'linkdin-3',
			link: '',
			height: '24.45px',
			width: '24.45px'
		},
		{
			icon: 'youtube-3',
			link: '',
			height: '26.08px',
			width: '26.08px'
		},
		{
			icon: 'instagram-3',
			link: '',
			height: '26.08px',
			width: '26.08px'
		}
	]

	const openLink = (e: string) => {
		navigateTo({
			hash: '#pricing'
		})
		productFilterKey.value = toLower(e).includes('senior') ? 'senior' : 'primecare'
	}
</script>

<template>
	<div
		class="bg-green-deep w-full"
		:class="{
			'pt-[48px] md:pt-0 min-h-[900px] flex flex-col items-end justify-end': viewKey === 'sendcare-waitlist'
		}"
	>
		<sendcare-wrapper class-name="w-full">
			<div class="md:pt-[151px] pb-[99px] sm:pt-[100px]">
				<div
					class="w-full flex-wrap md:gap-x-[133px] md:gap-y-[40px] gap-[20px] pb-[44px] md:border-b border-b-[#113932]"
					:class="{
						'item-center flex': viewKey === 'sendcare',
						'justify-between md:flex': viewKey !== 'sendcare'
					}"
				>
					<div
						v-if="viewKey === 'sendcare'"
						class="space-y-[23px] flex-none w-full md:w-auto border-b border-[#113932] md:border-none pb-[51px] md:pb-0 mb-[30px] md:mb-0"
					>
						<tools-icon
							dir="internal-logo"
							icon="sendcare-white"
							svg-class="md:w-[220px] md:h-[44px] h-[28.72px] w-[145px]"
							class="flex-none"
						/>
						<img
							src="/img/others/compliance.png"
							:height="129"
							:width="220"
							class="max-w-[220px] max-h-[129px] w-full h-full hidden md:block"
						/>
						<img
							src="/img/others/compliance-2.png"
							:height="129"
							:width="220"
							class="max-w-[104px] max-h-[37.9371px] w-full h-full md:hidden object-cover"
						/>
					</div>
					<tools-icon
						v-else-if="viewKey === 'sendcare-waitlist'"
						svg-class="sm:w-[620px] sm:h-[122.5px] w-[100%] h-[65.59px] mb-[80px]  md:mb-0"
						dir="internal-logo"
						icon="sendcare-white"
					/>
					<footer-link-structure
						:link-list="footerLinks"
						:class="[
							{
								'bg-[#0A332E] max-w-[414px] rounded-xl md:py-[30px] md:px-[35px] px-[14px] py-[20px] w-full':
									viewKey != 'sendcare'
							},
							'flex-1'
						]"
						title-cls="text-[#70A59A] uppercase font-semibold"
						:title-wrapper-cls="[viewKey === 'sendcare' ? 'border-b' : 'border-b-0', 'text-white']"
						li-cls="text-base-50  border-[#113932] border-b-[0.5px]"
						:btn-cls="[
							'md:h-auto w-[20px] h-[20px] rounded-full  items-center justify-center md:inline bg-[#347469] md:bg-transparent',
							viewKey === 'sendcare' ? 'flex' : 'hidden'
						]"
						@on-link-click="e => openLink(e || '')"
					>
						<template #btn-icon="{ isOpen }">
							<span v-if="viewKey === 'sendcare'" class="font-bold md:hidden text-success-900">{{
								isOpen ? '-' : '+'
							}}</span>
						</template>
					</footer-link-structure>
				</div>
				<div class="flex items-center md:pt-[44px] flex-wrap-reverse justify-between md:gap-[20px] gap-[38px]">
					<p class="text-[#4A6A63] text-base md:leading-[140%] max-w-[832px]">
						<span>
							<span class="font-[800]">SendCare</span> is a product of
							<span class="font-[800]">MyCoverGenius</span>, a leading insurtech platform in Nigeria.
						</span>
						<br />
						<br />
						All healthcare plans offered through SendCare are co-created in partnership with licensed and
						leading insurers in Nigeria. Buying a plan under SendCare, you are agreeing to mycovergenius
						terms and Conditions
					</p>

					<ul class="flex items-center gap-[8px]">
						<li v-for="social in socials" :key="social.icon" class="group">
							<a :href="social.link" target="_blank" :aria-label="`Follow us on ${social.icon}`">
								<tools-icon
									:icon="social.icon"
									dir="socials"
									svg-class=" h-[25.42px] w-[24.86px]"
									:path-class="`${
										social.icon === 'x'
											? 'group-hover:fill-[#E6F4F2]'
											: 'group-hover:stroke-[#E6F4F2]'
									} custom-transition-2`"
								/>
							</a>
						</li>
					</ul>
				</div>
			</div>
		</sendcare-wrapper>
	</div>
</template>

<style></style>
